import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

/**
 * OAuth 回調處理頁面
 * 處理 OAuth 提供商回調後的 token 存儲和重定向
 */
const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuthStatus } = useAuth();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('正在處理登入...');

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        // 從 URL 參數取得 token 和 provider
        const token = searchParams.get('token');
        const provider = searchParams.get('provider');
        const error = searchParams.get('error');

        // 檢查是否有錯誤
        if (error) {
          setStatus('error');
          const errorMessage = searchParams.get('message') || '登入失敗';
          setMessage(errorMessage);
          return;
        }

        // 檢查是否有 token
        if (!token) {
          setStatus('error');
          setMessage('無法取得授權 token');
          return;
        }

        // 調用後端 API 設置 HttpOnly Cookie
        const response = await api.post('/oauth/set-session', { token });

        if (response.data.success) {
          setStatus('success');
          setMessage(`${provider ? provider.charAt(0).toUpperCase() + provider.slice(1) : 'OAuth'} 登入成功!`);

          // 等待 cookie 設置生效,然後重新檢查登入狀態
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // 重新檢查登入狀態
          await checkAuthStatus();
          
          // 再等一下確保狀態更新
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // 重定向到 Dashboard
          navigate('/dashboard', { replace: true });
        } else {
          setStatus('error');
          setMessage('設置登入 session 失敗');
        }

      } catch (err) {
        console.error('OAuth callback 處理錯誤:', err);
        setStatus('error');
        setMessage('處理登入回調時發生錯誤');
      }
    };

    handleOAuthCallback();
  }, [searchParams, navigate, checkAuthStatus]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md shadow-xl bg-white rounded-lg p-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">
            {status === 'processing' && '⏳ 處理中'}
            {status === 'success' && '✅ 登入成功'}
            {status === 'error' && '❌ 登入失敗'}
          </h2>
          <p className="text-gray-600 mb-6">OAuth 身份驗證</p>
        </div>

        <div className={`p-6 rounded-lg text-center ${
          status === 'processing' ? 'bg-blue-50 border border-blue-200' :
          status === 'success' ? 'bg-green-50 border border-green-200' :
          'bg-red-50 border border-red-200'
        }`}>
          {/* 載入動畫 */}
          {status === 'processing' && (
            <div className="flex justify-center mb-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {/* 成功圖示 */}
          {status === 'success' && (
            <div className="flex justify-center mb-4">
              <svg className="w-16 h-16 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}

          {/* 錯誤圖示 */}
          {status === 'error' && (
            <div className="flex justify-center mb-4">
              <svg className="w-16 h-16 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          )}

          <p className={`text-lg font-medium ${
            status === 'processing' ? 'text-blue-900' :
            status === 'success' ? 'text-green-900' :
            'text-red-900'
          }`}>
            {message}
          </p>

          {status === 'error' && (
            <button
              onClick={() => navigate('/login')}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              返回登入頁面
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OAuthCallback;
